<template>
  <div v-if='notice'>
    <wc-page-title :loading='isLoading' :title='title' :subtitle='subtitle' subtitleBreak>
      <template v-slot:rightSide>
        <wc-burger-menu>

          <v-list-item @click='showHelpDialog = true'>
            <v-list-item-icon>
              <v-icon color='info'>mdi-help-circle-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Editing Help</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item @click="editNotice">
            <v-list-item-icon>
              <v-icon>mdi-pencil</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Edit Notice Details</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item @click="openPreviewDialog">
            <v-list-item-icon>
              <v-icon color='info'>mdi-file-eye-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Preview Notice</v-list-item-title>
              <!-- MJERROR - preview will fail outside of staging - this needs to be fixed -->
            </v-list-item-content>
          </v-list-item>

          <v-list-item :disabled='notice.system_notice' @click="sendNotice">
            <v-list-item-icon>
              <v-icon :disabled='notice.system_notice' color='primary'>mdi-file-send-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Send Notice</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item v-if='canDebug' @click="debug = !debug">
            <v-list-item-icon>
              <v-icon color='primary'>mdi-file-code-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ debug ? "Exit " : "Enter " }}Debug mode</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

        </wc-burger-menu>
      </template>
    </wc-page-title>

    <router-view v-if='notice' :notice='notice' :isLoading='isLoading' :debug='debug' @update='update'></router-view>

    <wc-dialog :showDialog='showHelpDialog' title='Notice Editor Help' icon='mdi-help-circle-outline' width='800'
      hideCancel saveText='OK' @save='showHelpDialog = false'>
      <span v-html='helpText'></span>
    </wc-dialog>

    <notice-details-dialog ref='noticeDialog' :showDialog='showDialog' :loading='isLoading' v-model='noticeCopy'
      @cancel='cancel' @save='save'></notice-details-dialog>

    <wc-dialog :showDialog='showPreviewDialog' :loading='isLoadingPreview' title='Preview Setup' icon='mdi-file-find'
      width='980' :disableButtons='!isFormValid || !previewSystemID' saveText='Preview' cancelText='Close'
      :loadingButtons='isLoadingPreview' @save='viewNotice' @cancel='closePreview'>
      <template v-slot:toolbarButtons>
        <v-autocomplete class='mb-n7' width='0' dense v-if='true' label='Choose a conflict-free Project'
          v-model='previewSystemID' :items='eligibleSystemsList' item-value='id' item-text='project_display'
          :item-disabled='checkDisablePreview' :disabled='isLoadingPreview'></v-autocomplete>
      </template>

      <v-row>
        <v-col cols='5'>
        </v-col>
        <v-col cols='12'>
          <div v-if='noticePreview && !isLoading' style='color: #51545e !important;' v-html='noticePreview.text'></div>
        </v-col>
      </v-row>
    </wc-dialog>

  </div>
</template>

<script>
import _ from 'lodash';
import noticesAPI from '@/modules/notices/api/';
import NoticeDetailsDialog from '../components/NoticeDetailsDialog';
//import newNoticeAPI from '@/api/notices';

export default {
  name: 'notice-base',
  data: () => ({
    isLoading: true,
    isLoadingPreview: false,
    isFormValid: true,
    debug: false,
    notice: null,
    noticeCopy: {},
    noticePreview: {},
    previewSystemsList: [],
    previewSystemID: null,
    previewTimelineOverride: 1,
    showDialog: false,
    showHelpDialog: false,
    showPreviewDialog: false,
    timelineOptions: [
      { option: 1, text: "Use current proj setting" },
      { option: 2, text: "Pretend PO1 requested" },
      { option: 3, text: "Pretend PO1 NOT selected" },
    ],
    bdHelpText: "",
    // refer to set date values (computed) so user can see them
    suHelpText: "",
    // refer to set date values (computed) so user can see them
    helpText: "<b>Welcome to the new notice builder</b><br>" +
      "In order to facilitate easier editing and fewer total notices, there's a few things that have changed<br>" +
      "<br><b>Mentions</b> <i>(replacement text)</i><br>" +
      "Type the '@' symbol to bring up the list of possible options.<br>" +
      "This feature is used to insert values that are unique to the recipient (or the season).<br>" +
      "Mentions can be styled and highlighted as required.<br>" +
      "<br><b>Highlighting</b> <i>(conditionally rendered text)</i><br>" +
      "This text will be added to a notice only if certain conditions are met.<br>" +
      "For example, text highlighted in the 'Shutoff' color will only be displayed IF the project has one.<br>" +
      "Hosebib systems will not see text with the shutoff highlighting.<br>" +
      "Any text that should only appear under certain conditions needs to be highlighted.<br>" +
      "<br><b>Other Tidbits</b><br>" +
      "<ul><li>Remember to save your changes!<br>" +
      "<i>The 'save' button will activate if anything has changed.</i></li>" +
      "<li>Please note that double spaces are removed by the editor whenever a notice is loaded onto this page.<br>" +
      "<i>This will activate the save button, even though you didn't do anything.</i></li>" +
      "<li>If you don't want to save any changes, just navigate away from the page.</li>" +
      "<li>Preview a notice to see the final output - including the page headers.<br>" +
      "<i>The page headers cannot be edited here. (please contact Westcap if changes are required)</i></li>" +
      "<li>System Notices cannot be sent manually.<br>" +
      "<i>They are sent automatically when certain conditions are met (staging, work order completion, etc.)</i></li>" +
      "<li>Manual sending of custom notices is still under construction.</li>" +
      "</ul>"
  }),
  computed: {
    title() {
      if (this.notice) {
        return this.notice.name
      }
      return ''
    },
    subtitle() {
      if (this.notice) {
        return this.notice.description
      }
      return ''
    },
    menu() {
      return [
        {
          title: 'Overview',
          icon: 'mdi-email',
          route: { name: 'NoticeOverview', params: { id: this.$route.params.id } }
        }
      ]
    },
    eligibleSystemsList() {
      if (this.notice.division == _.find(this.$store.state.misc.divisions, d => d.division === 'Snow').id) {
        return this.previewSystemsList
      } else if (this.notice.for_com && this.notice.is_snail_mail) {
        return _.filter(this.previewSystemsList, s => s.is_comm && s.is_snail_mail)
      } else if (!this.notice.for_com && this.notice.is_snail_mail) {
        return _.filter(this.previewSystemsList, s => !s.is_comm && s.is_snail_mail)
      } else if (this.notice.for_com && !this.notice.is_snail_mail) {
        return _.filter(this.previewSystemsList, s => s.is_comm && !s.is_snail_mail)
      } else if (!this.notice.for_com && !this.notice.is_snail_mail) {
        return _.filter(this.previewSystemsList, s => !s.is_comm && !s.is_snail_mail)
      }
      return this.previewSystemsList
    },
    canDebug() {
      if (this.$store.state.auth.profile.id == 1 || this.$store.state.auth.profile.id == 11) {
        return true
      }
      return false
    }
  },
  mounted() {
    this.getNotice()
  },
  methods: {
    async getNotice() {
      this.isLoading
      this.notice = await noticesAPI.getNotice(this.$route.params.id)
      this.isLoading = false
    },
    async update(notice) {
      this.isLoading = true
      this.notice = await noticesAPI.updateNotice(notice)
      this.isLoading = false
    },
    async openPreviewDialog() {
      this.noticePreview = null
      this.showPreviewDialog = true
      if (!this.previewSystemsList.length) {
        this.isLoadingPreview = true
        if (this.notice.division == _.find(this.$store.state.misc.divisions, d => d.division == "Irrigation").id) {
          this.previewSystemsList = await noticesAPI.getIrrigationPreviewList()
        } else if (this.notice.division == _.find(this.$store.state.misc.divisions, d => d.division == "Snow").id) {
          this.previewSystemsList = await noticesAPI.getSnowPreviewList()
        } else if (this.notice.division == _.find(this.$store.state.misc.divisions, d => d.division == "Lawn").id) {
          this.previewSystemsList = await noticesAPI.getLawnPreviewList();
        }

        else {
          this.previewSystemsList = []
        }
        this.isLoadingPreview = false
      }
    },
    async viewNotice() {
      this.isLoadingPreview = true
      let obj = { project: this.previewSystemID }
      if (this.previewTimelineOverride !== null) {
        obj.timeline = this.previewTimelineOverride
      }
      this.noticePreview = await noticesAPI.previewNotice(this.notice.id, obj)
      this.isLoadingPreview = false
    },
    closePreview() {
      this.showPreviewDialog = false
      this.noticePreview = null
    },
    sendNotice() {
      //newNoticeAPI.sendNotice()
    },
    editNotice() {
      this.noticeCopy = _.cloneDeep(this.notice)
      this.showDialog = true
      this.$refs.noticeDialog.resetValidate()
    },
    async save() {
      this.isLoading = true
      this.notice = await noticesAPI.updateNotice(this.noticeCopy)
      this.showDialog = false
      this.isLoading = false
      if (this.notice.archived) {
        this.$router.push({ name: 'NoticesDashboard' })
      }
    },
    cancel() {
      this.$refs.noticeDialog.resetForm()
      this.showDialog = false
    },
    checkDisablePreview(item) {
      return item.conflict_count > 0
    }
  },
  components: {
    'notice-details-dialog': NoticeDetailsDialog
  }
}
</script>
